import labels from '../../../../../../../data/labels';
import Table from '../../../../../../table/Table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getInlineIcon } from '../../../../../../icon/Icon';
import { isArray, isLiteralObject, isNumber, isString } from '../../../../../../../helpers/typeHelpers';
import { TableCellValue, TableCellValueBase, TableData, TableHeader, TableRowsResponse } from '../../../../../../../models/SidePanelTypes';
import { createAssetActionLink } from '../../../useActions';
import { Icons } from '../../../../../../icon/icons/material';
import { ErrorHandler } from '../../../../../../../contexts/error-handler/ErrorHandler';
import { useChat } from '../../../../../../../contexts/chat/ChatContext';
import { guid } from '../../../../../../../helpers/randomHelpers';
import { useErrorHandlerContext } from '../../../../../../../contexts/error-handler/ErrorContext';
import { useLov } from '../../../../../../../hooks/useLabels';
import styles from '../Data.module.scss';
import { useTableForm } from './useTableForm';

type LabelResolver = (v: string | number | boolean, label?: string) => string;
const defaultLabelResolver: LabelResolver = ((v: string | number | boolean) => v?.toString() || "");

const DataTable = ({ className, headers, rows, title, source, chatId, form: actions }: TableData['properties'] & { className?: string; chatId?: string; }) => {
    const { tableData: getDynamicData } = useChat();
    const { errorId, getError, registerError, removeError } = useErrorHandlerContext();
    const error = useMemo(() => getError(errorId), [errorId, getError]);

    const [loadDynamicData, setLoadDynamicData] = useState<boolean>();
    const [dynamicData, setDynamicData] = useState<TableRowsResponse>();


    const initiallyLoadedRef = useRef(false);
    const loader = useCallback((async () => {

        if (!source || !chatId) return;
        setLoadDynamicData(true);
        removeError(errorId);

        setDynamicData(undefined);
        try {
            const data = await getDynamicData(chatId, source, undefined, {});
            setDynamicData(data);
            setLoadDynamicData(false);
        }
        catch (error: any) {
            console.error(error);
            setLoadDynamicData(false);
            registerError({ [errorId]: { type: 'notification', headline: labels.loadingErrorDescription, details: error } });
        }
        initiallyLoadedRef.current = true;
    }), [source, chatId, removeError, errorId, getDynamicData, registerError]);

    // Initial load
    useEffect(() => {
        loader();
    }, [loader]);

    const lov = useLov();
    const resolveLabel = useCallback<LabelResolver>((value, label) => {
        if (!label) return value?.toString();
        return (lov as Record<string, Record<string | number, string>>)[label]?.[value as string | number] ?? value?.toString() ?? "";
    }, [lov]);


    const form = useTableForm(chatId, headers, rows, actions);

    if (error) return <ErrorHandler.Notification className={styles.error} id={errorId} retry={{ headline: labels.retry, onAsyncClick: loader }} />;
    if (loadDynamicData === false && !dynamicData?.rows?.length) return <ErrorHandler.Notification isStandalone alertOverride={{ type: 'info', headline: labels.noDynamicData }} />;


    return (
        <>
            {title && <><h5>{title}</h5></>}
            <form onInput={form.handleTableFormInput} ref={form.tableFormRef}>
                <Table
                    className={className}
                    showSkeletonWhileLoading
                    pagination={
                        {
                            onPageSet: form.handleTablePageChange
                        }
                    }
                    // dynamicFilter={
                    //     <FilterControls controller={controller} filters={filters} />
                    // }
                    labels={{
                        placeholder: labels.tableSearchHeadline,
                        notFoundMessage: labels.tableSearchNotResultsFound
                    }}
                    title={""}
                    columns={
                        headers.map(({ name, sortable, searchable }) => {
                            return { id: name, Header: name, accessor: name, canBeSorted: !!sortable, isSearchable: searchable };
                        })
                    }
                    data={
                        (dynamicData?.rows || rows)
                            // ?.filter(row => {
                            //     if (!controller.input.length) return row;
                            //     const included = controller.input.find((x) => row.some((cell) => x.values.includes(cell)));
                            //     return included;
                            // })
                            ?.map((row, rowIndex) => {
                                return row.reduce((acc, curr, i) => {
                                    const header = headers[i];
                                    const value = processTableValue(curr, header, rowIndex, resolveLabel);
                                    acc[header?.name] = value;
                                    return acc;
                                }, {} as Record<string, string>);
                            })
                    }
                />
                {form.tableFormActions}
            </form>
        </>
    );
};

export default DataTable;

const processTableValue = (
    value: TableCellValue,
    header: TableHeader,
    rowIndex: number,
    resolveLabel: LabelResolver = defaultLabelResolver,
) => {
    if (header.form) {
        if (!isString(value) && !isNumber(value) && !("url" in value)) { // This indicates that the cell value contains a link. Links are not compatible with forms.
            return processTableCellForm(value, header, rowIndex, resolveLabel);
        }
    }
    const label = header.label;

    if (!resolveLabel)
        resolveLabel = ((v: string | Number | boolean) => v?.toString() || "");

    const resolveText = (value: string | number, label?: string) => {
        const text = resolveLabel!(value, label);
        if (text.length < 70)
            return text;
        else {
            const elmId = guid();
            return `<div class="collapsible">
                <input type="checkbox" class="collapsible-state" id="${elmId}" />
                <span>${text}</span>
                <label for="${elmId}" class="collapsible-cta collapsible-cta-more">...</label>
            </div>`;
        }
    };

    if (isString(value) || isNumber(value)) return resolveText(value, label);

    if (isArray(value)) {
        if (!value.length) return '';
        if (value.length === 1) return resolveText(value[0]?.toString(), label);

        const elmId = guid();
        const result: string[] = value.map(v => {
            let text;
            if (isString(v) || isNumber(v)) {
                text = resolveLabel!(v, label);
            }
            else if (isLiteralObject<{}>(v)) {
                // This scenario happens when CellFormValue is added to a row list value without defining a form in the cell header
                text = v.label ?? v.value ?? "";
            }

            return `<li>${text}</li>`;
        });
        return `
            <div class="collapsible">
                <input type="checkbox" class="collapsible-state" id="${elmId}" />
                <ul>
                    ${result.join('')}
                </ul>
                ${value.length > 3 ? `<label for="${elmId}" class="collapsible-cta collapsible-cta-more">...</label>` : ''}
            </div>
        `;
    }
    if (isLiteralObject(value)) {
        if ("value" in value) {
            // This scenario happens when CellFormValue is added to a row without defining a form in the cell header
            return resolveLabel(value.label ?? value.value, label);
        }

        if (value.text) return `<a href="${value.url}" class="${styles.link}">${resolveLabel(value.text, label)}</a>`;

        switch (value.icon) {
            case 'new-tab': return `<a href="${value.url}" class="${styles['icon-link']}" target="_blank" rel="noopener noreferrer">${getInlineIcon({ iconName: Icons?.[value.icon as keyof typeof Icons] })}</a>`;
            case 'download': return `<a href="${value.url}" class="${styles['icon-link']}" target="_blank" rel="noopener noreferrer" download>${getInlineIcon({ iconName: Icons?.[value.icon as keyof typeof Icons] })}</a>`;
            case 'pdf': return `<a href="#${createAssetActionLink(value.url)}" class="${styles['icon-link']}">${getInlineIcon({ iconName: Icons?.[value.icon as keyof typeof Icons] })}</a>`;
            default: return '-';
        }
    }

    return '';
};

function processTableCellForm(
    value: TableCellValueBase,
    header: TableHeader,
    rowIndex: number,
    resolveLabel: LabelResolver = defaultLabelResolver,
) {
    const form = header.form;
    if (!form) {
        return "";
    }
    if (!isArray(value)) {
        value = [value];
    }
    const formValues = value.map(v => {
        if (isString(v) || isNumber(v)) {
            return { label: v?.toString(), value: v };
        }
        else {
            return v;
        }
    });

    const formFiledName = `${rowIndex}-${form.name}`;


    const formElements: string[] = [];
    if (form.type === "checkbox") {
        formValues.forEach((v, i) => {

            formElements.push(`<label><input type="checkbox" name="${formFiledName}" value="${v.value}"/><span>${resolveLabel(v.label || v.value, header.label)}</span></label>`);
        });
    }
    else if (form.type === "radio") {
        formValues.forEach(v => {
            formElements.push(`<label><input type="radio" name="__ui-${formFiledName}" value="${v.value}"/><span>${resolveLabel(v.label || v.value, header.label)}</span></label>`);
        });
        formElements.push(`<label><input type="${form.allow_custom ? "text" : "hidden"}" name="${formFiledName}"/></label>`);
    }
    const formDiv = `<div class="form">${formElements.join('')}<form>`;

    return formDiv;
}
